import './SprayZoneChart.scss';

import { useCallback, useMemo } from 'react';

import { SprayZoneDto } from 'types/CodegenReportTypes';

import { getOrderedColorSet } from 'utilities/colorHelpers';
import { useChartDataDisplay } from 'hooks/helperHooks';

enum ZoneName {
  RightInfield = 'Right Infield',
  RightCenterInfield = 'Right Center Infield',
  LeftCenterInfield = 'Left Center Infield',
  LeftInfield = 'Left Infield',
  RightField = 'Right Field',
  RightCenterField = 'Right Center Field',
  CenterField = 'Center Field',
  LeftCenterField = 'Left Center Field',
  LeftField = 'Left Field',
}

type Props = {
  sprayZones: SprayZoneDto[];
  isLoading: boolean;
};

const SprayZoneChart = ({ sprayZones, isLoading }: Props) => {
  const simulateChartData = useCallback((isLoading: boolean) => {
    return isLoading ? Object.values(ZoneName).map((zoneName) => ({ zoneName, percentage: Math.floor(Math.random() * 50), count: 0 })) : [];
  }, []);

  const chartDataDisplay = useChartDataDisplay(sprayZones, simulateChartData, isLoading);

  const mappedSprayZones = useMemo(() => {
    return new Map<ZoneName, number>(chartDataDisplay.map((sprayZone) => [sprayZone.zoneName as ZoneName, sprayZone.percentage]));
  }, [chartDataDisplay]);

  function getZoneColors(zoneName: ZoneName): { bg: string | undefined; text: string } {
    const percentage = mappedSprayZones.get(zoneName);
    return percentage ? getOrderedColorSet(10, 30, percentage) : { bg: undefined, text: '#414141' };
  }

  function getZoneDisplayText(zoneName: ZoneName): string {
    const percentage = mappedSprayZones.get(zoneName);
    return `${percentage ?? '-'}%`;
  }

  return (
    <div className='SprayZoneChart-container'>
      <svg className='SprayZoneChart-chart' viewBox='0 0 296 257' fill='none' xmlns='http://www.w3.org/2000/svg'>
        {/* Right Infield */}
        {(() => {
          const colors = getZoneColors(ZoneName.RightInfield);
          const displayText = getZoneDisplayText(ZoneName.RightInfield);
          return (
            <>
              <path
                d='M239.192 164.2C230.675 147.758 217.853 133.905 202.221 124.128C202.211 124.123 202.206 124.118 202.195 124.112L147.689 255.703L239.192 164.206'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={210} y={165} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Right Center Infield */}
        {(() => {
          const colors = getZoneColors(ZoneName.RightCenterInfield);
          const displayText = getZoneDisplayText(ZoneName.RightCenterInfield);
          return (
            <>
              <path
                d='M147.689 255.703V108.518C167.705 108.518 186.391 114.231 202.195 124.108L147.689 255.703Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={170} y={145} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Left Center Infield */}
        {(() => {
          const colors = getZoneColors(ZoneName.LeftCenterInfield);
          const displayText = getZoneDisplayText(ZoneName.LeftCenterInfield);
          return (
            <>
              <path
                d='M147.689 255.703L93.1836 124.108L93.2098 124.092C109.014 114.22 127.685 108.518 147.689 108.518V108.764V255.703Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={125} y={145} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Left Infield */}
        {(() => {
          const colors = getZoneColors(ZoneName.LeftInfield);
          const displayText = getZoneDisplayText(ZoneName.LeftInfield);
          return (
            <>
              <path
                d='M147.69 255.703L56.1875 164.2C64.7094 147.748 77.5368 133.885 93.1839 124.112L147.69 255.703Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={85} y={165} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Right Field */}
        {(() => {
          const colors = getZoneColors(ZoneName.RightField);
          const displayText = getZoneDisplayText(ZoneName.RightField);
          return (
            <>
              <path
                d='M211.428 130.61C222.905 139.665 232.411 151.117 239.191 164.201L294.884 108.507C288.057 82.7374 274.032 60.8284 255.743 43.6328L211.428 130.61Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={260} y={100} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Right Center Field */}
        {(() => {
          const colors = getZoneColors(ZoneName.RightCenterField);
          const displayText = getZoneDisplayText(ZoneName.RightCenterField);
          return (
            <>
              <path
                d='M170.594 111.076L187.24 5.97217C212.497 12.637 236.483 25.5219 255.74 43.6329L211.425 130.61C208.479 128.287 205.409 126.121 202.223 124.128C192.623 118.128 181.967 113.66 170.594 111.076Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={205} y={70} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Center Field */}
        {(() => {
          const colors = getZoneColors(ZoneName.CenterField);
          const displayText = getZoneDisplayText(ZoneName.CenterField);
          return (
            <>
              <path
                d='M124.781 111.076L108.135 5.97741C121.14 2.54562 134.48 0.761719 147.689 0.761719H147.695C160.904 0.761719 174.239 2.54562 187.239 5.97741L170.592 111.081C163.227 109.407 155.557 108.523 147.684 108.523C139.811 108.523 132.142 109.407 124.776 111.081L124.781 111.076Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={145} y={50} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Left Center Field */}
        {(() => {
          const colors = getZoneColors(ZoneName.LeftCenterField);
          const displayText = getZoneDisplayText(ZoneName.LeftCenterField);
          return (
            <>
              <path
                d='M83.9499 130.61L39.6348 43.6378C58.8967 25.5267 82.8774 12.6418 108.135 5.97705L124.781 111.076C113.429 113.655 102.788 118.107 93.2042 124.091C89.9973 126.095 86.9108 128.276 83.9499 130.61Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={85} y={70} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}

        {/* Left Field */}
        {(() => {
          const colors = getZoneColors(ZoneName.LeftField);
          const displayText = getZoneDisplayText(ZoneName.LeftField);
          return (
            <>
              <path
                d='M39.636 43.6382C21.368 60.8285 7.3165 82.7585 0.5 108.513L56.1881 164.201C62.968 151.112 72.4734 139.666 83.9511 130.61L39.636 43.6382Z'
                stroke='#414141'
                strokeWidth='1'
                fill={colors.bg}
              />
              <text x={35} y={100} fill={colors.text}>
                {displayText}
              </text>
            </>
          );
        })()}
      </svg>
    </div>
  );
};

export default SprayZoneChart;
